import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  archivePlatformTenant,
  clearSelectedPlatformTenant,
  deletePlatformTenantLicense,
  getPlatformTenant,
  getSelectedPlatformTenant,
  mapLaravelError,
  restorePlatformTenant,
  selectPlatformTenant,
  testPlatformTenantDatabaseConnection,
} from '../../services/platformTenantsApi';
import {
  formatLicenseSummary,
  formatLicenseWindow,
  licenseStatusColor,
  licenseStatusLabel,
} from '../../utils/tenantLicense';

const TENANT_BASE_PATH = '/super-admin/platform/tenants';

const statusColor = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'active') return 'success';
  if (value === 'suspended') return 'warning';
  if (value === 'archived') return 'default';
  return 'default';
};

const DetailItem = ({ label, value }) => (
  <Stack spacing={0.35}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
      {value || '-'}
    </Typography>
  </Stack>
);

const ColorSwatch = ({ label, value }) => (
  <Stack direction="row" spacing={1.2} alignItems="center">
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: value || '#ffffff',
      }}
    />
    <Stack spacing={0.1}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {value || '-'}
      </Typography>
    </Stack>
  </Stack>
);

const AssetPreview = ({ label, url }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Stack spacing={1}>
      <Typography variant="subtitle2">{label}</Typography>
      {url ? (
        <>
          <Box
            component="img"
            src={url}
            alt={label}
            sx={{
              maxWidth: '100%',
              maxHeight: 96,
              objectFit: 'contain',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
              bgcolor: 'background.default',
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {url}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Not configured.
        </Typography>
      )}
    </Stack>
  </Paper>
);

const MetricTile = ({ label, value, helper }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  </Paper>
);

export default function TenantDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const tenantId = Number(id || 0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [databaseCheck, setDatabaseCheck] = useState(null);
  const [databaseCheckLoading, setDatabaseCheckLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [selectedTenantKey, setSelectedTenantKey] = useState(() => getSelectedPlatformTenant()?.key || '');

  const loadTenant = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getPlatformTenant(tenantId);
      setPayload(response || null);
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const tenant = payload?.tenant || null;
  const bootstrap = payload?.bootstrap || null;
  const branding = bootstrap?.branding || {};
  const featureFlags = Object.entries(bootstrap?.feature_flags || {});
  const domains = tenant?.domains || [];
  const databaseConnection = tenant?.database_connection || {};
  const hrAdmin = payload?.hr_admin || null;
  const metrics = payload?.metrics || null;
  const currentLicense = payload?.current_license || null;
  const licenseUsage = payload?.license_usage || null;
  const licenseStatus = payload?.license_status || '';
  const nextLicense = payload?.next_license || null;
  const licenseHistory = Array.isArray(payload?.license_history) ? payload.license_history : [];
  const isSelectedTenant = selectedTenantKey === tenant?.key;

  const handleArchiveTenant = async () => {
    if (!tenant || !window.confirm(`Archive ${tenant.display_name}?`)) {
      return;
    }

    setError('');
    try {
      const response = await archivePlatformTenant(tenant.id);
      setPayload((previous) => ({
        ...previous,
        tenant: response?.tenant || { ...tenant, status: 'archived' },
      }));
      if (isSelectedTenant) {
        clearSelectedPlatformTenant();
        setSelectedTenantKey('');
      }
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleRestoreTenant = async () => {
    if (!tenant) {
      return;
    }

    setError('');
    try {
      const response = await restorePlatformTenant(tenant.id);
      setPayload((previous) => ({
        ...previous,
        tenant: response?.tenant || { ...tenant, status: 'active' },
      }));
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleDeleteFutureLicense = async (licenseRecord) => {
    if (!tenant || !licenseRecord || !window.confirm(`Delete future license ${formatLicenseWindow(licenseRecord)}?`)) {
      return;
    }

    setError('');
    try {
      await deletePlatformTenantLicense(tenant.id, licenseRecord.id);
      await loadTenant();
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    }
  };

  const handleTestDatabaseConnection = async () => {
    if (!tenant?.id) {
      return;
    }

    setDatabaseCheckLoading(true);
    setDatabaseCheck(null);
    setError('');

    try {
      const response = await testPlatformTenantDatabaseConnection(tenant.id);
      setDatabaseCheck(response || null);
    } catch (requestError) {
      const mapped = mapLaravelError(requestError);
      setDatabaseCheck({
        ok: false,
        message: mapped.message,
      });
    } finally {
      setDatabaseCheckLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Alert severity="error">{error || 'Tenant not found.'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {tenant.display_name}
              </Typography>
              <Chip size="small" color={statusColor(tenant.status)} label={tenant.status || 'unknown'} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Tenant identity, routing, branding, and isolated database configuration.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(TENANT_BASE_PATH)}>
              Back
            </Button>
            <Button
              variant={isSelectedTenant ? 'contained' : 'outlined'}
              startIcon={<CheckCircleOutlineOutlinedIcon />}
              onClick={() => {
                if (!tenant) return;
                selectPlatformTenant(tenant);
                setSelectedTenantKey(tenant.key);
              }}
            >
              {isSelectedTenant ? 'Selected' : 'Select Tenant'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ManageAccountsOutlinedIcon />}
              onClick={() => {
                if (!tenant) return;
                selectPlatformTenant(tenant);
                setSelectedTenantKey(tenant.key);
                navigate('/admin/users');
              }}
            >
              Manage Users
            </Button>
            <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => navigate(`${TENANT_BASE_PATH}/${tenant.id}/edit`)}>
              Edit
            </Button>
            {String(tenant.status).toLowerCase() === 'archived' ? (
              <Button variant="outlined" startIcon={<RestoreOutlinedIcon />} onClick={handleRestoreTenant}>
                Restore
              </Button>
            ) : (
              <Button variant="outlined" color="error" startIcon={<ArchiveOutlinedIcon />} onClick={handleArchiveTenant}>
                Archive
              </Button>
            )}
          </Stack>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Licenses and Capacity
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddOutlinedIcon />}
                        onClick={() => navigate(`${TENANT_BASE_PATH}/${tenant.id}/licenses/new`)}
                      >
                        Add / Renew License
                      </Button>
                      {nextLicense?.can_edit ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => navigate(`${TENANT_BASE_PATH}/${tenant.id}/licenses/${nextLicense.id}/edit`)}
                        >
                          Edit Future License
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <MetricTile
                        label="Seats Used"
                        value={licenseUsage?.used ?? 'N/A'}
                        helper="Active users"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MetricTile
                        label="Provisioned Users"
                        value={metrics?.users?.total ?? 'N/A'}
                        helper="All user records"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MetricTile
                        label="Active Seat Cap"
                        value={licenseUsage?.limit ?? 'Not set'}
                        helper={licenseUsage?.remaining != null ? `${licenseUsage.remaining} remaining` : 'No active license'}
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip color={licenseStatusColor(licenseStatus)} label={licenseStatusLabel(licenseStatus)} size="small" />
                    <Typography variant="body2" color="text.secondary">
                      {currentLicense ? `Current window: ${formatLicenseWindow(currentLicense)}` : 'No current license window.'}
                    </Typography>
                  </Stack>

                  {currentLicense ? (
                    <Alert severity={licenseStatusColor(licenseStatus) === 'error' ? 'error' : 'info'}>
                      {formatLicenseSummary(licenseUsage)}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      This tenant cannot add or reactivate users until an active license window is created.
                    </Alert>
                  )}

                  {nextLicense ? (
                    <Alert severity="info">
                      Next license: {formatLicenseWindow(nextLicense)}
                    </Alert>
                  ) : null}

                  {Array.isArray(licenseUsage?.per_role) && licenseUsage.per_role.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Role</TableCell>
                            <TableCell>Used</TableCell>
                            <TableCell>Limit</TableCell>
                            <TableCell>Remaining</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {licenseUsage.per_role.map((roleLimit) => (
                            <TableRow key={roleLimit.role}>
                              <TableCell>{roleLimit.role}</TableCell>
                              <TableCell>{roleLimit.used ?? 'N/A'}</TableCell>
                              <TableCell>{roleLimit.limit}</TableCell>
                              <TableCell>{roleLimit.remaining ?? 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={roleLimit.over_limit ? 'error' : 'success'}
                                  label={roleLimit.over_limit ? 'Over Limit' : 'Within Limit'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : null}

                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      License History
                    </Typography>
                    {licenseHistory.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No license history recorded for this tenant yet.
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Window</TableCell>
                              <TableCell>Seat Cap</TableCell>
                              <TableCell>Role Caps</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {licenseHistory.map((licenseRecord) => (
                              <TableRow key={licenseRecord.id}>
                                <TableCell>{formatLicenseWindow(licenseRecord)}</TableCell>
                                <TableCell>{licenseRecord.total_active_users_limit}</TableCell>
                                <TableCell>
                                  {Array.isArray(licenseRecord.role_limits) && licenseRecord.role_limits.length > 0
                                    ? licenseRecord.role_limits.map((roleLimit) => `${roleLimit.role}: ${roleLimit.active_user_limit}`).join(', ')
                                    : 'No role caps'}
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" color={licenseStatusColor(licenseRecord.window_status)} label={licenseStatusLabel(licenseRecord.window_status)} />
                                </TableCell>
                                <TableCell align="right">
                                  {licenseRecord.can_edit ? (
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                      <Button
                                        size="small"
                                        startIcon={<EditOutlinedIcon />}
                                        onClick={() => navigate(`${TENANT_BASE_PATH}/${tenant.id}/licenses/${licenseRecord.id}/edit`)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteOutlineOutlinedIcon />}
                                        onClick={() => handleDeleteFutureLicense(licenseRecord)}
                                      >
                                        Delete
                                      </Button>
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      Locked
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Stack>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Tenant Basics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Display name" value={tenant.display_name} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Short name" value={tenant.short_name} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Tenant key" value={tenant.key} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Org code" value={tenant.org_code} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Product label" value={tenant.product_label} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Auth mode" value={tenant.auth_mode} />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Domains
                  </Typography>
                  {domains.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {domains.map((domain) => (
                        <Chip
                          key={domain.id || domain.domain}
                          label={domain.domain}
                          color={domain.is_primary ? 'primary' : 'default'}
                          variant={domain.is_primary ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No domains configured.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Database Connection
                      </Typography>
                      {metrics?.available === false ? (
                        <Chip size="small" color="error" label="Unavailable" />
                      ) : metrics?.available === true ? (
                        <Chip size="small" color="success" label="Available" />
                      ) : null}
                    </Stack>
                    <Button size="small" variant="outlined" onClick={handleTestDatabaseConnection} disabled={databaseCheckLoading}>
                      {databaseCheckLoading ? 'Testing...' : 'Test Connection'}
                    </Button>
                  </Stack>
                  {databaseCheck ? (
                    <Alert severity={databaseCheck.ok ? 'success' : 'error'}>
                      {databaseCheck.message || (databaseCheck.ok ? 'Tenant database connection is healthy.' : 'Tenant database connection failed.')}
                    </Alert>
                  ) : metrics?.available === false ? (
                    <Alert severity="warning">
                      Tenant database metrics are unavailable. Test the connection or edit the saved database credentials.
                    </Alert>
                  ) : null}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Driver" value={databaseConnection.driver} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Database" value={databaseConnection.database} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Host" value={databaseConnection.host} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Port" value={databaseConnection.port} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Username" value={databaseConnection.username} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Charset" value={databaseConnection.charset} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Collation" value={databaseConnection.collation} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Unix socket" value={databaseConnection.unix_socket} />
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Tenant HR Admin
                  </Typography>
                  {hrAdmin ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <DetailItem label="Name" value={hrAdmin.name} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailItem label="Employee code" value={hrAdmin.employee_code} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DetailItem label="Email" value={hrAdmin.email} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <DetailItem label="Gender" value={hrAdmin.gender} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <DetailItem label="Status" value={hrAdmin.status} />
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No HR Admin has been provisioned for this tenant yet.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Runtime Branding
                  </Typography>
                  <DetailItem label="Bootstrap display name" value={branding.display_name} />
                  <DetailItem label="Bootstrap short name" value={branding.short_name} />
                  <DetailItem label="Email from name" value={branding.email_from_name} />
                  <Divider />
                  <ColorSwatch label="Primary color" value={branding.primary_color} />
                  <ColorSwatch label="Secondary color" value={branding.secondary_color} />
                  <ColorSwatch label="Accent color" value={branding.accent_color} />
                </Stack>
              </Paper>

              <AssetPreview label="Logo (Light)" url={branding.logo_light_url} />
              <AssetPreview label="Logo (Dark)" url={branding.logo_dark_url} />
              <AssetPreview label="Favicon" url={branding.favicon_url} />
              <AssetPreview label="PDF Header Logo" url={branding.pdf_header_logo_url} />

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Feature Flags
                  </Typography>
                  {featureFlags.length ? (
                    featureFlags.map(([key, value]) => (
                      <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {key}
                            </Typography>
                            <Chip size="small" color={value?.enabled ? 'success' : 'default'} label={value?.enabled ? 'Enabled' : 'Disabled'} />
                          </Stack>
                          <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                            {value?.value ? JSON.stringify(value.value, null, 2) : 'No feature payload'}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No feature flags configured.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
